import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, Info, Leaf, Octagon, Search, TriangleAlert, X } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
const ALLEGATO = 'DM 10 agosto 2018, Allegato 1';
const ALLEGATO_URL = 'https://www.gazzettaufficiale.it/eli/id/2018/09/26/18A06095/SG';
const APPORTI = 'Ministero della Salute — Apporti giornalieri (Sett. 2021)';
const APPORTI_URL = 'https://www.salute.gov.it/new/sites/default/files/imported/C_17_pagineAree_1268_5_file.pdf';
type Citation = {
  text: string;
  url?: string;
};
type ItVerdict = 'Permitted' | 'Permitted with conditions' | 'Verification required' | 'Exceeds limit' | 'Not permitted';
type ItIssue = {
  title: string;
  chip: string;
  body: string;
  citations: Citation[];
};
type ItIngredient = {
  id: string;
  name: string;
  amount?: string;
  verdict: ItVerdict;
  limit: string;
  source?: Citation;
};
type ItGroup = {
  title: string;
  ratio: string;
  ingredients: ItIngredient[];
};
const blockers: ItIssue[] = [{
  title: "Confirm vitamin E dose and reduce if over 60 mg/day",
  chip: 'Vitamin E (Mixed Tocopherols)',
  body: "The formulation calculates 67 mg/day from the Mixed Tocopherol raw material against Italy's 60 mg/day ceiling — 7 mg over if the declared figure represents α-tocopherol equivalents (α-TE). Confirm the expression basis with the raw-material supplier; if confirmed, the dose must be reduced.",
  citations: [{
    text: APPORTI,
    url: APPORTI_URL
  }, {
    text: 'Regulation (EC) No 1170/2009',
    url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX%3A32009R1170'
  }]
}, {
  title: 'Confirm species and plant part for the witch hazel extract',
  chip: 'Declared as Hamamelis mollis (flower)',
  body: "The Allegato 1 list admits only Hamamelis virginiana L., bark and leaf — not Hamamelis mollis, and not flower as a part for either species. As declared, this ingredient has no basis on the admitted list by species or by part. Get written confirmation of the actual species and part from the supplier; if Hamamelis mollis flower is genuinely what's in the formula, it needs a full admissibility review before notification.",
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }, {
    text: 'EMA/HMPC/114585/2008',
    url: 'https://www.ema.europa.eu/en/documents/herbal-report/assessment-report-hamamelis-virginiana-l-cortex-hamamelis-virginiana-l-folium-hamamelis-virginiana-l-folium-et-cortex-aut-ramunculus-destillatum_en.pdf'
  }]
}, {
  title: 'Reduce horse chestnut dose to bring aescin under 75 mg/day',
  chip: 'Horse chestnut extract (Aesculus hippocastanum, seed)',
  body: 'The Allegato 1 entry requires aescin titration on the label and caps daily aescin intake at 75 mg. This formula delivers 80 mg/day — 5 mg over. Species and part are correctly matched; the dose is the problem. Reduce the extract dose or its aescin standardisation, and add the mandatory titration statement regardless.',
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }]
}];
const shouldFix: ItIssue[] = [{
  title: 'Confirm copper holds the 15% NRV floor',
  chip: 'Copper',
  body: "Label claim is 0.15 mg/day (15% NRV) — exactly at Italy's minimum floor, with no margin. Confirm manufacturing/analytical tolerance keeps the actual batch content at or above the floor.",
  citations: [{
    text: APPORTI,
    url: APPORTI_URL
  }]
}, {
  title: 'Confirm manganese holds the 15% NRV floor',
  chip: 'Manganese',
  body: 'Label claim is 0.3 mg/day (15% NRV) — same situation as copper, exactly at the floor with no margin.',
  citations: [{
    text: APPORTI,
    url: APPORTI_URL
  }]
}, {
  title: 'Confirm citrus variety and synephrine content',
  chip: 'Bitter orange extract (hesperidin source, std. 90%)',
  body: "Latin binomial and plant part aren't declared. If this is Citrus aurantium var. amara (bitter orange), a mandatory synephrine titration label statement applies with a 30 mg/day synephrine ceiling plus a secondary octopamine limit. A separate, unrestricted Allegato 1 entry exists for the sweet-orange variety — so variety matters as much as species. Get the exact variety, binomial and part from the supplier in writing.",
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }]
}, {
  title: 'Confirm Latin binomial and plant part',
  chip: 'Apple extract (Applephenon, 70% polyphenols)',
  body: "Malus domestica Borkh. is admissible either as a recognised food ingredient or under Allegato 1 — but the spec doesn't state the binomial or part actually used.",
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }]
}, {
  title: 'Confirm Novel Food specification and add the medication-interaction warning',
  chip: 'Nattokinase (nattiase®, 20,000 FU/g)',
  body: "Authorised at a maximum of 100 mg/day, adults only, excluding pregnant and lactating women. This formula's 50 mg/day is within the cap, but the branded source hasn't been confirmed against the authorised specification, and the mandatory medical-supervision statement is missing from the label.",
  citations: [{
    text: 'Commission Implementing Decision (EU) 2017/115',
    url: 'https://eur-lex.europa.eu/eli/dec_impl/2017/115/oj/eng'
  }]
}, {
  title: 'Confirm Latin binomial',
  chip: 'Grape seed extract (95% OPC)',
  body: "Vitis vinifera L. seed is an admitted part with no restriction, but the binomial isn't stated. The formula also contains a separate Vitis vinifera leaf extract — keep the species/part pairing clearly documented for both.",
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }]
}, {
  title: 'Confirm Latin binomial',
  chip: 'Gotu kola extract (aerial parts)',
  body: "Centella asiatica (L.) Urb., admitted parts match the declared 'aerial parts' with no restriction — but the binomial isn't stated in writing.",
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }]
}, {
  title: 'Confirm the exact plant part used',
  chip: 'Cypress twig oil (Cupressus sempervirens)',
  body: 'Allegato 1 admits cone, bark, leaf, galbuli, wood and essential oil for this species — twig/branch is not among the six named parts, and the product is declared specifically as twig oil. Confirm with the supplier exactly which part the essential oil is distilled from.',
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }]
}, {
  title: 'Confirm Latin binomial',
  chip: 'Olive leaf-and-fruit extract',
  body: "The ingredient name specifies leaf and fruit; 'Olive tree extract' is a marketing label, not the operative declaration. Olea europaea L. admits both parts with no restriction — but the binomial isn't stated.",
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }]
}];
const considers: ItIssue[] = [{
  title: 'Document cumulative coumarin exposure',
  chip: 'Sweet clover extract (Melilotus officinalis)',
  body: "No coumarin ceiling in Italian law. At 4 mg coumarin/day the product sits under both the EFSA TDI-derived ceiling (6 mg/day) and ANSES's more conservative recommendation (4.8 mg/day) — close enough to the ANSES figure that background dietary coumarin is worth accounting for in the safety file.",
  citations: [{
    text: ALLEGATO,
    url: ALLEGATO_URL
  }, {
    text: 'ANSES opinion',
    url: 'https://www.anses.fr/en/content/beware-overconsumption-herbal-supplements-containing-coumarin'
  }]
}, {
  title: 'Consolidate three overlapping anticoagulant warnings',
  chip: 'Nattokinase + sweet clover + horse chestnut',
  body: 'All three carry an anticoagulant/antiplatelet caution of uneven regulatory weight: sweet clover carries a formal EU regulatory contraindication, while horse chestnut and nattokinase rest on clinical/pharmacovigilance literature rather than a legal instrument. Consolidate into a single warning statement.',
  citations: [{
    text: 'EMA/HMPC European Union herbal monograph on Melilotus officinalis',
    url: 'https://www.ema.europa.eu/en/documents/herbal-monograph/final-european-union-herbal-monograph-melilotus-officinalis-l-lam-herba_en.pdf'
  }]
}];
const groups: ItGroup[] = [{
  title: 'Vitamins & minerals',
  ratio: '5/5',
  ingredients: [{
    id: 'it-vitamin-e',
    name: 'Vitamin E (Mixed Tocopherols)',
    amount: '67 mg/day',
    verdict: 'Exceeds limit',
    limit: 'Italian ceiling 60 mg/day, pending confirmation of expression basis — exceeds by 7 mg if confirmed',
    source: {
      text: APPORTI,
      url: APPORTI_URL
    }
  }, {
    id: 'it-thiamin',
    name: 'Thiamin (Vitamin B1)',
    amount: '4.4 mg/day (400% NRV)',
    verdict: 'Permitted',
    limit: 'Italian ceiling 25 mg/day — comfortably within it',
    source: {
      text: APPORTI,
      url: APPORTI_URL
    }
  }, {
    id: 'it-copper',
    name: 'Copper',
    amount: '0.15 mg/day (15% NRV)',
    verdict: 'Permitted with conditions',
    limit: "At Italy's 15% NRV minimum floor — no margin",
    source: {
      text: APPORTI,
      url: APPORTI_URL
    }
  }, {
    id: 'it-manganese',
    name: 'Manganese',
    amount: '0.3 mg/day (15% NRV)',
    verdict: 'Permitted with conditions',
    limit: 'Same floor as copper — no margin',
    source: {
      text: APPORTI,
      url: APPORTI_URL
    }
  }, {
    id: 'it-vitamin-c',
    name: 'Vitamin C',
    amount: '100 mg/day (125% NRV)',
    verdict: 'Permitted',
    limit: 'Italian ceiling 1,000 mg/day — well within it',
    source: {
      text: APPORTI,
      url: APPORTI_URL
    }
  }]
}, {
  title: 'Botanicals, other actives & excipients',
  ratio: '14/14',
  ingredients: [{
    id: 'it-bitter-orange',
    name: 'Bitter orange extract (Citrus aurantium, hesperidin source)',
    verdict: 'Verification required',
    limit: 'Variety, binomial and part not yet confirmed — see Should fix.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-apple',
    name: 'Apple extract (Applephenon, 70% polyphenols)',
    verdict: 'Verification required',
    limit: 'Admissible either way — binomial and part not yet confirmed.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-nattokinase',
    name: 'Nattokinase (nattiase®, 20,000 FU/g)',
    amount: '50 mg/day',
    verdict: 'Permitted with conditions',
    limit: 'Within the 100 mg/day Novel Food cap — supplier spec and label warning still outstanding.',
    source: {
      text: 'Commission Implementing Decision (EU) 2017/115',
      url: 'https://eur-lex.europa.eu/eli/dec_impl/2017/115/oj/eng'
    }
  }, {
    id: 'it-grape-seed',
    name: 'Grape seed extract (95% OPC)',
    verdict: 'Verification required',
    limit: 'Vitis vinifera L., seed — admitted, no restriction — binomial not yet confirmed.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-gotu-kola',
    name: 'Gotu kola extract (aerial parts)',
    verdict: 'Verification required',
    limit: 'Centella asiatica (L.) Urb. — admitted, no restriction — binomial not yet confirmed.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-cypress',
    name: 'Cypress twig oil',
    verdict: 'Verification required',
    limit: 'Twig/branch not among the six Allegato 1 admitted parts for this species.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-threonine',
    name: 'L-Threonine',
    verdict: 'Permitted',
    limit: 'Free-form amino acid, established pre-1997 use — not Novel Food listed.',
    source: {
      text: 'Regulation (EU) 2015/2283',
      url: 'https://eur-lex.europa.eu/eli/reg/2015/2283/oj/eng'
    }
  }, {
    id: 'it-hamamelis',
    name: 'Witch hazel extract, declared as Hamamelis mollis (flower)',
    verdict: 'Not permitted',
    limit: 'No basis on Allegato 1 by species or by part as declared — see Blocker.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-melilotus',
    name: 'Sweet clover extract (Melilotus officinalis)',
    verdict: 'Permitted with conditions',
    limit: 'No restriction attached — document cumulative coumarin exposure.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-horse-chestnut',
    name: 'Horse chestnut extract (Aesculus hippocastanum, seed)',
    amount: '80 mg aescin/day',
    verdict: 'Exceeds limit',
    limit: '5 mg over the 75 mg/day aescin ceiling — see Blocker.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-ruscus',
    name: "Butcher's broom root extract (Ruscus aculeatus)",
    verdict: 'Permitted',
    limit: 'Root matches declaration, no restriction, unremarkable dose.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-red-vine',
    name: 'Red vine leaf extract (Vitis vinifera, leaf, EFLA-type)',
    verdict: 'Permitted',
    limit: 'Leaf matches declaration — keep distinguished from the seed extract above.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-olive',
    name: 'Olive leaf-and-fruit extract',
    verdict: 'Verification required',
    limit: 'Both parts admitted, no restriction — binomial not yet confirmed.',
    source: {
      text: ALLEGATO,
      url: ALLEGATO_URL
    }
  }, {
    id: 'it-hpmc',
    name: 'Capsule shell (HPMC, E464)',
    verdict: 'Permitted',
    limit: 'Standard EU-authorised food additive, permitted quantum satis.',
    source: {
      text: 'Regulation (EC) No 1333/2008, Annex II',
      url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32008R1333'
    }
  }]
}];
const filters: {
  id: string;
  label: string;
  verdict?: ItVerdict;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'permitted',
  label: 'Permitted 6',
  verdict: 'Permitted'
}, {
  id: 'conditions',
  label: 'Permitted with conditions 4',
  verdict: 'Permitted with conditions'
}, {
  id: 'verification',
  label: 'Verification required 6',
  verdict: 'Verification required'
}, {
  id: 'exceeds',
  label: 'Exceeds limit 2',
  verdict: 'Exceeds limit'
}, {
  id: 'not-permitted',
  label: 'Not permitted 1',
  verdict: 'Not permitted'
}];
export function ItalyFormulaTab() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const visibleGroups = useMemo(() => {
    const verdict = filters.find(filter => filter.id === activeFilter)?.verdict;
    const term = search.trim().toLowerCase();
    return groups.map(group => ({
      ...group,
      ingredients: group.ingredients.filter(ingredient => (!verdict || ingredient.verdict === verdict) && (!term || ingredient.name.toLowerCase().includes(term)))
    })).filter(group => group.ingredients.length > 0);
  }, [activeFilter, search]);
  return <div className="space-y-5" data-id="element-841" data-mp="1ncsjterc5wyo">
      <section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" aria-labelledby="it-formula-heading" data-id="element-842" data-mp="1nag314xbdsn7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-843" data-mp="uo3ro589d1z2">
          <div className="flex items-start gap-3" data-id="element-844" data-mp="19c84wtojklag">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-845" data-mp="19dsx4cndvs55">
              <Leaf className="h-5 w-5" data-id="element-846" data-mp="14jyj9pugm7dz" />
            </span>
            <div data-id="element-847" data-mp="guvyi94j3yf9">
              <h2 id="it-formula-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-848" data-mp="gucfep3573zn">
                Ingredients analysis
              </h2>
              <p className="mt-1 text-sm text-[#718174]" data-id="element-849" data-mp="2byak8faq9k6p">Deterministic per-ingredient verdict (EU / Italy).</p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-800" data-id="element-850" data-mp="2gwc06rcux3dy">
            High risk
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs font-semibold" data-id="element-851" data-mp="1t0r832sz3lg2">
          <span className="text-red-700" data-id="element-852" data-mp="1xzaut5fe519h">3 Blocker</span>
          <span className="text-amber-700" data-id="element-853" data-mp="1y041k6ecnqrm">9 Should fix</span>
          <span className="text-[#718174]" data-id="element-854" data-mp="220web9mfkdsn">2 Consider</span>
        </div>

        <p className="mt-4 max-w-4xl text-sm leading-relaxed text-[#56685c]" data-id="element-855" data-mp="270915udaxkv8">
          Three ingredients carry compliance-blocking issues: vitamin E may exceed Italy&apos;s ceiling, one botanical has no basis on the
          admitted-ingredients list as declared, and horse chestnut&apos;s aescin dose exceeds the mandatory cap. Nine further ingredients need
          supplier documentation — mostly missing Latin binomial and plant-part confirmation — before the file is complete.
        </p>
      </section>

      <section aria-label="Blocker" className="space-y-3" data-id="element-856" data-mp="3it1a6hyof0r6">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-red-700" data-id="element-857" data-mp="3irggeuk6rlud">Blocker · 3</h2>
        {blockers.map(item => <article key={item.title} className="rounded-2xl border-2 border-red-300 border-l-[6px] border-l-red-600 bg-red-50 p-5 shadow-sm" data-id="element-858" data-mp="39t4q6tn8eq2g">
            <div className="flex items-start gap-3" data-id="element-859" data-mp="34sgtpsg8hh0u">
              <Octagon className="mt-0.5 h-5 w-5 shrink-0 fill-red-600 text-red-600" data-id="element-860" data-mp="2ztx51ka99r4s" />
              <div className="min-w-0" data-id="element-861" data-mp="2vtlk06dzdoss">
                <h3 className="font-[Figtree] text-base font-semibold text-red-950" data-id="element-862" data-mp="2vuoc2hn1hxaa">{item.title}</h3>
                <span className="mt-2 inline-flex rounded-full bg-white/70 px-2.5 py-1 text-xs font-medium text-red-900" data-id="element-863" data-mp="y2sg4vzpj4o5">{item.chip}</span>
                <p className="mt-3 text-sm leading-relaxed text-red-900" data-id="element-864" data-mp="1dagp8x188tvk">{item.body}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-865" data-mp="1dculvabk2e58">
                  {item.citations.map(citation => <RegulationLink key={citation.text} text={citation.text} href={citation.url} data-id="element-866" data-mp="13kx6rshff218" />)}
                </div>
              </div>
            </div>
          </article>)}
      </section>

      <section aria-label="Should fix" className="space-y-3" data-id="element-867" data-mp="tm7ymrfc0vy6">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-800" data-id="element-868" data-mp="tou3uker2zd8">Should fix · 9</h2>
        {shouldFix.map(item => <article key={`${item.title}-${item.chip}`} className="rounded-2xl border border-[#dce3da] border-l-4 border-l-amber-400 bg-white p-5 shadow-sm" data-id="element-869" data-mp="kn4d7gsp04gi">
            <div className="flex items-start gap-3" data-id="element-870" data-mp="2f03s17p90umq">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-871" data-mp="2f0luim2i8533" />
              <div className="min-w-0" data-id="element-872" data-mp="2jxm3p5unhnyf">
                <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-873" data-mp="2jwkpisic8lch">{item.title}</h3>
                <span className="mt-2 inline-flex rounded-full bg-[#f0f3ef] px-2.5 py-1 text-xs font-medium text-[#617366]" data-id="element-874" data-mp="2p3r6zgqgaggb">{item.chip}</span>
                <p className="mt-3 text-sm leading-relaxed text-[#56685c]" data-id="element-875" data-mp="32w7cqpaqkskx">{item.body}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-876" data-mp="2y475scw90zeu">
                  {item.citations.map(citation => <RegulationLink key={citation.text} text={citation.text} href={citation.url} data-id="element-877" data-mp="3cs20tzxqbfv3" />)}
                </div>
              </div>
            </div>
          </article>)}
      </section>

      <section aria-label="Consider" className="space-y-3" data-id="element-878" data-mp="3lt8k2grrj4s1">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#718174]" data-id="element-879" data-mp="3hs6k1xq0uop0">Consider · 2</h2>
        {considers.map(item => <article key={item.title} className="rounded-2xl bg-[#f7faf5] p-5" data-id="element-880" data-mp="1rhtt5wn0mzap">
            <div className="flex items-start gap-3" data-id="element-881" data-mp="1ng87pmwf1cow">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#8b968d]" data-id="element-882" data-mp="uros3y32q7j0" />
              <div className="min-w-0" data-id="element-883" data-mp="ptdgpix4royo">
                <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-884" data-mp="psc8rnbevil6">{item.title}</h3>
                <span className="mt-2 inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-medium text-[#617366]" data-id="element-885" data-mp="14gyk7dxx3q74">{item.chip}</span>
                <p className="mt-3 text-sm leading-relaxed text-[#56685c]" data-id="element-886" data-mp="zl9qvschr5j9">{item.body}</p>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-887" data-mp="bvox0eesunv7">
                  {item.citations.map(citation => <RegulationLink key={citation.text} text={citation.text} href={citation.url} data-id="element-888" data-mp="6wudfa90cva1" />)}
                </div>
              </div>
            </div>
          </article>)}
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="it-verdicts-heading" data-id="element-889" data-mp="1w8wlrltx2ful">
        <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-890" data-mp="1s72q5rykw21a">
          <h2 id="it-verdicts-heading" className="inline-flex items-center gap-2 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-891" data-mp="1zqu3l3zznj8t">
            <Info className="h-4 w-4 text-[#738478]" data-id="element-892" data-mp="24s1al0ub4eef" />
            Ingredient verdicts
          </h2>
          <p className="mt-3 font-mono text-xs font-semibold uppercase tracking-[0.11em] text-[#6f8174]" data-id="element-893" data-mp="1puqokxk1fq4g">
            6 permitted · 4 permitted with conditions · 6 verification required · 2 exceeds limit · 1 not permitted
          </p>
        </div>

        <div className="flex flex-col gap-3 border-b border-[#e5ebe3] px-5 py-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-894" data-mp="2ngof9i8kn1rh">
          <div className="flex flex-wrap gap-2" data-id="element-895" data-mp="28r0dkfwha138">
            {filters.map(filter => <button key={filter.id} type="button" onClick={() => setActiveFilter(filter.id)} aria-pressed={activeFilter === filter.id} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${activeFilter === filter.id ? 'border-forest-green bg-forest-green text-white' : 'border-[#dce3da] bg-white text-[#5f7266] hover:border-[#b8c6ba]'}`} data-id="element-896" data-mp="2dqcwwqo54q12">
                {filter.label}
              </button>)}
          </div>
          <label className="relative lg:w-72" data-id="element-897" data-mp="322v6tq6stca4">
            <span className="sr-only" data-id="element-898" data-mp="2x4jxdgkf13sf">Search ingredients</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9aa79c]" data-id="element-899" data-mp="2x5uzzd24k5hy" />
            <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search ingredients…" className="w-full rounded-lg border border-[#dce3da] bg-white py-2 pl-9 pr-3 text-sm text-evergreen placeholder:text-[#9aa79c] focus:border-forest-green focus:outline-none" data-id="element-900" data-mp="2sbqvd0gc09k1" />
          </label>
        </div>

        <div className="space-y-5 p-5" data-id="element-901" data-mp="ox6yilnujuyv">
          {visibleGroups.length === 0 && <p className="px-1 text-sm italic text-[#9aa79c]" data-id="element-902" data-mp="oz1853to40nn">No ingredients in this view.</p>}
          {visibleGroups.map(group => <div key={group.title} className="overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-903" data-mp="yul4frc591cx">
              <div className="flex items-center justify-between bg-[#f4f7f2] px-4 py-2.5" data-id="element-904" data-mp="12x7y7xsdkwcr">
                <p className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-905" data-mp="66p9os64xjis">{group.title}</p>
                <span className="font-mono text-xs font-semibold text-[#728477]" data-id="element-906" data-mp="b5a7kkxwzr42">{group.ratio}</span>
              </div>
              <div className="divide-y divide-[#edf0eb]" data-id="element-907" data-mp="f6vlm9m6qbfu">
                {group.ingredients.map(ingredient => <IngredientRow key={ingredient.id} ingredient={ingredient} open={Boolean(expanded[ingredient.id])} onToggle={() => setExpanded(state => ({
              ...state,
              [ingredient.id]: !state[ingredient.id]
            }))} data-id="element-908" data-mp="jyn8oa64tz18" />)}
              </div>
            </div>)}
        </div>
      </section>
    </div>;
}
function IngredientRow({
  ingredient,
  open,
  onToggle
}: {
  ingredient: ItIngredient;
  open: boolean;
  onToggle: () => void;
}) {
  return <div className="bg-white" data-id="element-909" data-mp="39honkn2teu4u">
      <div className="flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-start lg:justify-between" data-id="element-910" data-mp="39gn0fk8iyh6r">
        <div className="min-w-0 flex-1" data-id="element-911" data-mp="3n7icf5ebleek">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-912" data-mp="3namr4wpevsjh">
            <p className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-913" data-mp="3i976v6v25q25">{ingredient.name}</p>
            {ingredient.amount && <span className="text-sm text-[#718174]" data-id="element-914" data-mp="z91uf92ff8q">— {ingredient.amount}</span>}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-[#748477]" data-id="element-915" data-mp="3s620pcgdz2a3">{ingredient.limit}</p>
          {ingredient.source && <p className="mt-2 text-xs text-[#8b968d]" data-id="element-916" data-mp="1xu57pf2apocx">
              Source: <RegulationLink text={ingredient.source.text} href={ingredient.source.url} className="align-middle" data-id="element-917" data-mp="1xvyeqvgtmalg" />
            </p>}
          {open && <div className="mt-2 rounded-lg bg-[#f4f7f2] p-3 text-xs leading-relaxed text-[#607266]" data-id="element-918" data-mp="i9mcy6i8l2ao">
              Verdicts are assessed against the Italian admitted-ingredients list and the Ministry&apos;s daily intake levels, using the declared
              ingredient name, form and quantity per daily dose.
            </div>}
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end" data-id="element-919" data-mp="3usciouwm584n">
          <ItBadge verdict={ingredient.verdict} data-id="element-920" data-mp="3uq82pelfwio0" />
          <button type="button" onClick={onToggle} aria-expanded={open} className="rounded-lg p-1.5 text-[#718174] hover:bg-[#f2f5f1]" data-id="element-921" data-mp="3gtcutpgxcx9j">
            <ChevronDown className={`h-4 w-4 transition ${open ? 'rotate-180' : ''}`} data-id="element-922" data-mp="3lrwhjs3ced2y" />
          </button>
        </div>
      </div>
    </div>;
}
function ItBadge({
  verdict
}: {
  verdict: ItVerdict;
}) {
  if (verdict === 'Permitted') return <span className="inline-flex items-center gap-1.5 rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-923" data-mp="1ijnemm4ot5hs">
        <Check className="h-3.5 w-3.5" data-id="element-924" data-mp="1dig8ssm3ugdr" />
        Permitted
      </span>;
  if (verdict === 'Permitted with conditions') return <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-925" data-mp="18mrf35wrntpd">Permitted with conditions</span>;
  if (verdict === 'Verification required') return <span className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-926" data-mp="218u0gi0s2d7t">Verification required</span>;
  if (verdict === 'Exceeds limit') return <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800" data-id="element-927" data-mp="1rihh2t4p8q39">Exceeds limit</span>;
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800" data-id="element-928" data-mp="1mimrcswnu6js">
      <X className="h-3.5 w-3.5" data-id="element-929" data-mp="2f9mz9ey43ugq" />
      Not permitted
    </span>;
}